import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import { MessageSquare, Brain, Heart, BookOpen, Quote } from 'lucide-react';
import { SEO } from '../components/SEO';

export default function About() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollection('about_sections').then(data => {
      setSections(data);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-800"></div>
    </div>
  );

  const orderedTitles = ["Pædagogisk erfaring og ståsted", "Familien", "Uddannelse"];
  const orderedSections = [...sections].sort((a, b) => {
    const indexA = orderedTitles.indexOf(a.title.da);
    const indexB = orderedTitles.indexOf(b.title.da);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return (
    <div className="pb-24">
      <SEO 
        title="Hvem er jeg? Mød Anette Lundager Schädler | Vejen til barnet"
        description="Mød Anette Lundager Schädler. Med 25 års erfaring som pædagog og terapeut hjælper jeg forældre og fagfolk med at skabe trygge rammer og stærke relationer."
        path="/hvem-er-jeg"
      />
      {/* Hero Section */}
      <section className="relative h-[55vh] md:h-[65vh] min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden mb-0">
        <img 
          src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/02cce628-1075-4a14-83cb-0b4389d9071d.png" 
          alt="Børn der løber på en eng" 
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-stone-900/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 md:pt-32">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-light text-white uppercase tracking-tight mb-4"
          >
            Hvem er jeg
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg md:text-xl text-stone-200 max-w-2xl mx-auto font-light leading-relaxed"
          >
            Lær mig at kende, min faglige baggrund og de værdier, der driver mit arbejde som pædagogisk konsulent.
          </motion.p>
        </div>

        {/* Wave Divider at bottom of Hero */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
          <svg 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none" 
            className="relative block w-full h-12 md:h-24 fill-[#f1f2f1]"
          >
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </section>

      {/* Sektionerne under "Pædagogisk erfaring og ståsted" */}
      {/* Uddannelse & Efteruddannelse (4 Kasser) - Full Bleed Background */}
      <section
        className="mb-8 bg-gradient-to-b from-[#4e6354]/8 via-[#4e6354]/2 to-transparent pt-16 md:pt-24 pb-8 md:pb-12 border-0 w-full"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mb-12">
            <span className="text-xs font-semibold tracking-widest text-[#4e6354] uppercase block mb-2">Faglig efteruddannelse</span>
            <h2 className="text-3xl md:text-4xl font-light text-stone-900 mb-6 uppercase tracking-wide">
              Uddannelse & Efteruddannelse
            </h2>
            <p className="text-lg text-stone-600 leading-relaxed mb-6">
              Jeg blev uddannet pædagog i 1996. Siden dengang har jeg arbejdet med mennesker i mange forskellige sammenhænge.<br className="hidden md:inline" /> For at kunne hjælpe bedst muligt, har jeg løbende uddannet mig og bygget nye redskaber på min erfaring.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Theraplay */}
            <div className="bg-white border border-stone-200/60 p-6 md:p-8 rounded-xl shadow-sm hover:shadow-md hover:border-[#4e6354]/30 transition-all duration-300 flex flex-col justify-between group">
              <div>
                <div className="w-12 h-12 rounded-lg bg-[#4e6354]/10 flex items-center justify-center text-[#4e6354] mb-6 group-hover:bg-[#4e6354] group-hover:text-white transition-colors duration-300">
                  <Heart className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-stone-900 text-lg md:text-xl mb-3 tracking-wide">
                  Theraplay
                </h3>
                <p className="text-stone-600 leading-relaxed text-sm md:text-base">
                  Legeorienterede aktiviteter, der styrker tilknytningen.
                </p>
              </div>
              <div className="w-8 h-1 bg-stone-200 mt-6 group-hover:w-16 group-hover:bg-[#4e6354] transition-all duration-300"></div>
            </div>

            {/* Marte Meo */}
            <div className="bg-white border border-stone-200/60 p-6 md:p-8 rounded-xl shadow-sm hover:shadow-md hover:border-[#4e6354]/30 transition-all duration-300 flex flex-col justify-between group">
              <div>
                <div className="w-12 h-12 rounded-lg bg-[#4e6354]/10 flex items-center justify-center text-[#4e6354] mb-6 group-hover:bg-[#4e6354] group-hover:text-white transition-colors duration-300">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-stone-900 text-lg md:text-xl mb-3 tracking-wide">
                  Marte Meo
                </h3>
                <p className="text-stone-600 leading-relaxed text-sm md:text-base">
                  Fokus på den gode og udviklende dialog.
                </p>
              </div>
              <div className="w-8 h-1 bg-stone-200 mt-6 group-hover:w-16 group-hover:bg-[#4e6354] transition-all duration-300"></div>
            </div>

            {/* Neuroaffektiv psykoterapi */}
            <div className="bg-white border border-stone-200/60 p-6 md:p-8 rounded-xl shadow-sm hover:shadow-md hover:border-[#4e6354]/30 transition-all duration-300 flex flex-col justify-between group">
              <div>
                <div className="w-12 h-12 rounded-lg bg-[#4e6354]/10 flex items-center justify-center text-[#4e6354] mb-6 group-hover:bg-[#4e6354] group-hover:text-white transition-colors duration-300">
                  <Brain className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-stone-900 text-lg md:text-xl mb-3 tracking-wide">
                  Neuroaffektiv
                </h3>
                <p className="text-stone-600 leading-relaxed text-sm md:text-base">
                  Forståelse for hjernens og følelsernes udvikling.
                </p>
              </div>
              <div className="w-8 h-1 bg-stone-200 mt-6 group-hover:w-16 group-hover:bg-[#4e6354] transition-all duration-300"></div>
            </div>

            {/* Narrativ terapi */}
            <div className="bg-white border border-stone-200/60 p-6 md:p-8 rounded-xl shadow-sm hover:shadow-md hover:border-[#4e6354]/30 transition-all duration-300 flex flex-col justify-between group">
              <div>
                <div className="w-12 h-12 rounded-lg bg-[#4e6354]/10 flex items-center justify-center text-[#4e6354] mb-6 group-hover:bg-[#4e6354] group-hover:text-white transition-colors duration-300">
                  <BookOpen className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-stone-900 text-lg md:text-xl mb-3 tracking-wide">
                  Narrativ terapi
                </h3>
                <p className="text-stone-600 leading-relaxed text-sm md:text-base">
                  Fokus på de fortællinger, der former vores liv.
                </p>
              </div>
              <div className="w-8 h-1 bg-stone-200 mt-6 group-hover:w-16 group-hover:bg-[#4e6354] transition-all duration-300"></div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Venstre side: Billede i fuld højde */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            {orderedSections.find(s => s.image && s.image[0]) && (
              <div className="relative">
                <img 
                  src={orderedSections.find(s => s.image && s.image[0]).image[0]} 
                  alt="Familien og pædagogisk ståsted"
                  className="shadow-xl w-full h-auto object-cover rounded-3xl"
                />
                
                {/* Floating Quote Box */}
                <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white/95 backdrop-blur-md rounded-2xl p-6 border border-stone-200/50 shadow-xl max-w-[280px] sm:max-w-[320px] z-10">
                  <Quote className="w-8 h-8 text-[#4e6354]/20 absolute top-4 left-4" />
                  <p className="text-stone-700 italic text-sm md:text-base leading-relaxed relative z-10 pl-4 mt-2">
                    "Min lange erfaring og mine uddannelser gør, at jeg har en bred vifte af metoder til at møde jer lige dér, hvor I har brug for det."
                  </p>
                </div>
              </div>
            )}
          </motion.div>

          {/* Højre side: Teksterne stacket over hinanden */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-7 space-y-6"
          >
            {orderedSections.map((section) => (
              <div key={section.id} className="border-b border-stone-100 last:border-0 pb-6 last:pb-0">
                <h2 className="text-2xl md:text-3xl font-light text-stone-900 mb-4 uppercase tracking-wide">{section.title.da}</h2>
                <div 
                  className="prose prose-stone text-stone-600 mb-6 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: section.content.da }}
                />
                {section.title.da === "Uddannelse" && (
                  <Link 
                    to="/kontakt"
                    className="inline-block bg-stone-900 text-white px-8 py-3 rounded-none hover:bg-stone-800 transition-colors uppercase tracking-widest text-sm"
                  >
                    Kontakt mig
                  </Link>
                )}
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
